import React from 'react';
import PropTypes from 'prop-types';
import { NavLink, useRouteMatch } from 'react-router-dom';
import {
  ChevronFilledRight,
  ChevronFilledDown,
} from 'AppSrc/core/components/common/svg-icons';

// Component for the decoration for a side menu item
// If an SVG prop is provided show that
// If not and the menu has children, display a chevron that points right
// or down depending on whether the children are showing
// if there are no children, display a vertical line
const SideNavMenuItemDecoration = React.memo(({
  SVG,
  hasSubItems,
  showSubMenu,
}) => {
  if (!SVG && !hasSubItems) {
    return (
      <div className="vertical-line-container">
        <div className="vertical-line" />
      </div>
    );
  }
  const Chevron = showSubMenu ? ChevronFilledDown : ChevronFilledRight;
  const svgElem = SVG ? <SVG /> : <Chevron />;
  return (
    <div className="svg-wrapper">
      {svgElem}
    </div>
  );
});

SideNavMenuItemDecoration.propTypes = {
  SVG: PropTypes.elementType,
  hasSubItems: PropTypes.bool,
  showSubMenu: PropTypes.bool,
};

// custom hook to check if the item is active
// this replicates the functionality of navLink, but allows us to provide an alternative path
// It's just a function that calls a hook, but by convention I have to start the function name with `use`
// https://reactjs.org/docs/hooks-custom.html
// it's better than putting all the logic inside the component though
const useIsActive = ({
  matchPath,
  path,
  exact,
}) => {
  // if a matchPath was provided, match against that.
  // Otherwise match against the route path
  const pathToMatch = matchPath ? matchPath : path;
  // useRouteMatch is a hook from react-router to match location against a path
  const match = useRouteMatch(pathToMatch);
  const pathMatches = Boolean(match);
  const exactMatch = pathMatches && match.isExact;

  // the route is active if there is a path match
  // and if the exact prop is true, the match also has to be exact
  return exact ? exactMatch : pathMatches;
};

const SideNavMenuItem = React.memo(({
  menuItem,
  menuOpen,
  nestedLevel = 0,
}) => {
  const {
    path,
    SVG,
    text,
    exact,
    subItems,
    // matchPath is the path to match against to calculate if the link is active
    // if this is different from the path prop
    matchPath,
  } = menuItem;
  // calculate if the link is active
  const isActive = useIsActive({
    matchPath,
    path,
    exact,
  });
  const hasSubitems = Boolean(subItems);
  // only show sub menu, if there are sub items, and the menu is open
  // and the current route is active
  const showSubMenu = menuOpen && hasSubitems && isActive;

  const isNested = nestedLevel > 0;
  const sideNavClass = 'side-nav-button';
  const classNames = [sideNavClass];
  if (isNested) {
    classNames.push(`${sideNavClass}-nested`);
  }
  if (!hasSubitems) {
    classNames.push(`${sideNavClass}-nochildren`);
  }
  if (isActive) {
    classNames.push(`${sideNavClass}-selected`);
  }
  return (
    <>
      <NavLink
        className={classNames.join(' ')}
        exact={exact}
        to={path}
      >
        <div className="side-nav-button-inner">
          <SideNavMenuItemDecoration
            SVG={SVG}
            showSubMenu={showSubMenu}
            hasSubItems={hasSubitems}
          />
          {
            menuOpen && (
              <div className="text-wrapper">
                <p>{text}</p>
              </div>
            )
          }
        </div>
      </NavLink>
      {
        showSubMenu ? (
          <div className="side-nav-sub-items">
            {subItems.map((subItem) => (
              <SideNavMenuItem
                menuItem={subItem}
                menuOpen={menuOpen}
                nestedLevel={nestedLevel + 1}
                key={subItem.path}
              />
            ))}
          </div>
        ) : null
      }
    </>
  );
});

const menuItemProps = PropTypes.shape({
  path: PropTypes.string,
  SVG: PropTypes.elementType,
  text: PropTypes.string,
  exact: PropTypes.bool,
  subItems: PropTypes.arrayOf(PropTypes.object),
  matchPath: PropTypes.string,
});

SideNavMenuItem.propTypes = {
  menuItem: menuItemProps,
  menuOpen: PropTypes.bool,
  nestedLevel: PropTypes.number,
};

export { SideNavMenuItem };
